import type { DesignProblem } from '@pz/scene';
import { el, iconSvg, note, type DesignerCtx } from './panels.js';
import { isBlocking, problemMessage } from './texts.js';
import { templateUnreplaced } from './commands.js';

/**
 * The Pregled tab's right-hand column: the last look before "Gotovo".
 *
 * Two lists, and the difference between them matters. The CHECKLIST is the
 * reference editor's four reminders — the things only a human can verify, like
 * whether a name is spelt the way the recipient spells it. The PROBLEM list
 * below it is machine-checked and blocking: every line there is a reason the
 * till would refuse the order, with a button that selects the offending layer
 * and opens its tool, so "Ispravi" means one click rather than a hunt.
 *
 * Three of the four reminders can be answered automatically (nothing is cut
 * off, every template object was replaced, there is only one side), so those
 * tick themselves and stop asking.
 */

export interface ReviewActions {
  /** Selects the layer a problem is about and opens its panel. */
  fix(problem: DesignProblem): void;
  done(): void;
  /** True when the product has more than one surface. */
  multiSurface: boolean;
}

interface Checked {
  names: boolean;
  sides: boolean;
}

const ticked = new WeakMap<DesignerCtx, Checked>();

function checksFor(ctx: DesignerCtx): Checked {
  const existing = ticked.get(ctx);
  if (existing) return existing;
  const fresh: Checked = { names: false, sides: false };
  ticked.set(ctx, fresh);
  return fresh;
}

export function renderReview(body: HTMLElement, ctx: DesignerCtx, actions: ReviewActions): void {
  const answer = ctx.store.value();
  const problems = ctx.problems();
  const blocking = problems.filter(isBlocking);
  const checks = checksFor(ctx);

  body.append(el('h3', 'pzd-review-title', ctx.texts.reviewTitle));
  body.append(el('p', 'pzd-hint', ctx.texts.reviewLead));

  const list = el('div', 'pzd-checklist');
  list.setAttribute('role', 'list');

  // Two the shopper answers…
  list.append(manualCheck(ctx.texts.checkNames, checks.names, (on) => {
    checks.names = on;
    ctx.refreshPanel();
  }));
  if (actions.multiSurface) {
    list.append(manualCheck(ctx.texts.checkSides, checks.sides, (on) => {
      checks.sides = on;
      ctx.refreshPanel();
    }));
  }

  // …and two the designer already knows.
  const cutOff = problems.some((problem) => problem.code === 'outside-zone');
  list.append(autoCheck(ctx.texts.checkCut, !cutOff));
  const unreplaced = answer.layers.filter(templateUnreplaced);
  if (answer.layers.some((layer) => layer.template)) {
    list.append(autoCheck(ctx.texts.checkTemplates, unreplaced.length === 0));
  }
  body.append(list);

  if (!blocking.length) {
    body.append(note(ctx.texts.noProblems));
  } else {
    body.append(el('div', 'pzd-lbl', ctx.texts.blockingCount.replace('{n}', String(blocking.length))));
    const problemList = el('div', 'pzd-problems');
    for (const problem of blocking) {
      const row = el('div', 'pzd-problem');
      row.append(el('span', undefined, problemMessage(problem)));
      const fix = el('button', 'pzd-ghost');
      fix.type = 'button';
      fix.textContent = ctx.texts.fixProblem;
      fix.addEventListener('click', () => actions.fix(problem));
      row.append(fix);
      problemList.append(row);
    }
    body.append(problemList);
  }

  const done = el('button', 'pzd-primary is-wide', ctx.texts.done);
  done.type = 'button';
  done.addEventListener('click', () => actions.done());
  body.append(done);
}

function manualCheck(label: string, on: boolean, onToggle: (next: boolean) => void): HTMLElement {
  const row = el('button', `pzd-check${on ? ' is-on' : ''}`);
  row.type = 'button';
  row.setAttribute('aria-pressed', String(on));
  const mark = el('span', 'pzd-check-mark');
  mark.innerHTML = iconSvg('check', 14);
  row.append(mark, el('span', undefined, label));
  row.addEventListener('click', () => { on = !on; row.classList.toggle('is-on', on); row.setAttribute('aria-pressed', String(on)); onToggle(on); });
  return row;
}

function autoCheck(label: string, ok: boolean): HTMLElement {
  const row = el('div', `pzd-check is-auto${ok ? ' is-on' : ' is-bad'}`);
  row.setAttribute('role', 'listitem');
  const mark = el('span', 'pzd-check-mark');
  mark.innerHTML = iconSvg(ok ? 'check' : 'warning', 14);
  row.append(mark, el('span', undefined, label));
  return row;
}
